// Reset / set password landing page — /auth/reset-password.
//
// Two ways a Supabase invite/recovery email can land here:
//
//  (A) token_hash flow (PREFERRED — pre-fetch-safe): the email template
//      links to this page directly with ?token_hash=...&type=invite|
//      recovery. Verification happens HERE via supabase.auth.verifyOtp,
//      i.e. only when a human loads the page and runs JS — so email
//      security scanners that GET the link can't consume the one-time
//      token before the user clicks (the failure that stranded invited
//      users on the sign-in page). Requires VITE_SUPABASE_* on the
//      frontend (every stride client has them for the browser login).
//
//  (B) implicit hash flow (fallback): Supabase's /auth/v1/verify endpoint
//      redirects here with #access_token=...&type=... — used if the email
//      template still points at the verify endpoint.
//
// Either way we end with a Supabase access token, then:
//   1. POST /api/auth/exchange → app JWT   2. set-password   3. login().
// Exchange requires a matching zebra_stride_user_account row — always
// true, since the admin routes insert the mirror row before provisioning.
//
// Reuses the Home page's landing styling so the set-password card looks
// identical to the sign-in card.

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@app/services/api';
import { useAuth } from '@platform/auth/frontend/contexts/AuthContext';
import { getSupabaseClient } from '@platform/auth-supabase/frontend/supabaseClient';
import strideLogo from '@app/assets/zebra-stride-logo.png';
import { HOME_URL } from '@platform/app-shell/frontend/utils/landing';
import '@platform/auth/frontend/pages/Home.css';

function parseParams() {
  const hash = window.location.hash || '';
  const h = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const q = new URLSearchParams(window.location.search || '');
  return {
    tokenHash: q.get('token_hash'),                       // flow (A)
    accessToken: h.get('access_token'),                   // flow (B)
    refreshToken: h.get('refresh_token'),                 // flow (B) — needed to install the session
    type: q.get('type') || h.get('type') || 'recovery',
    errorDescription:
      h.get('error_description') || q.get('error_description') ||
      ((h.get('error') || q.get('error')) ? 'This link is invalid.' : null),
  };
}

// sessionStorage marker set once THIS page has installed a recovery/invite
// session. Lets the flow survive a reload that happens after the one-time
// token was consumed and the URL scrubbed (a service-worker deploy-refresh,
// or a manual F5): the resolve effect finds no URL token, sees the marker,
// and recovers the persisted session instead of dead-ending a link that
// already verified. Tab-scoped, so a stale session from another user on a
// shared browser can never open this form by itself.
const RESET_MARKER_KEY = 'zi_pw_reset_type';

export default function ResetPassword() {
  const navigate = useNavigate();
  const { login } = useAuth();

  // link: null (resolving) | { accessToken, type }
  //     | { invalid, reason } | { retry, reason }
  const [link, setLink] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [mfaHandoff, setMfaHandoff] = useState(false); // password set, 2FA needed at sign-in
  const resolvedRef = useRef(false); // StrictMode double-invoke guard
  const retryRef = useRef(null); // in-memory tokens for a network-failure retry

  const markSession = (type) => { try { sessionStorage.setItem(RESET_MARKER_KEY, type); } catch { /* ignore */ } };
  const clearMarker = () => { try { sessionStorage.removeItem(RESET_MARKER_KEY); } catch { /* ignore */ } };
  // supabase-js tags fetch-level failures (offline, DNS, 5xx gateway) with
  // this name — the token was NOT judged by the auth server, so the link
  // is still good and the install can simply be retried.
  const isRetryable = (err) => err?.name === 'AuthRetryableFetchError';
  const RETRY_REASON = 'Could not reach the server — the link has not been used. Check your connection and try again.';

  // Flow (A): verify the one-time hash in the browser, now, as the
  // human — this is what makes it immune to email link pre-fetching.
  const installFromTokenHash = async ({ tokenHash, type }) => {
    try {
      const supabase = getSupabaseClient();
      // Purge any stale persisted session first (previous user on a
      // shared browser) so the verified invite/recovery session can
      // never be confused with it. Local-only: clears this browser's
      // storage without revoking the other user's sessions elsewhere.
      await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
      const { data, error: vErr } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type });
      if (vErr) throw vErr;
      const at = data?.session?.access_token;
      if (!at) throw new Error('No session returned from verification');
      markSession(type);
      retryRef.current = null;
      setLink({ accessToken: at, type });
    } catch (err) {
      if (isRetryable(err)) {
        retryRef.current = { flow: 'A', tokenHash, type };
        setLink({ retry: true, reason: RETRY_REASON });
      } else {
        setLink({ invalid: true, reason: 'This link has expired or was already used.' });
      }
    }
  };

  // Flow (B): the hash tokens ARE the session — install it in the
  // client so updateUser at submit targets THIS invite/recovery
  // user. Previously the raw token was only kept in React state
  // and the client was created at submit time: updateUser then
  // acted on whatever stale sb-* session localStorage held (a
  // previous user on a shared browser → silently changed the
  // WRONG user's password) or failed with "Auth session missing".
  const installFromHashTokens = async ({ accessToken, refreshToken, type }) => {
    try {
      const supabase = getSupabaseClient();
      await supabase.auth.signOut({ scope: 'local' }).catch(() => {});
      if (refreshToken) {
        const { error: sErr } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken,
        });
        if (sErr) throw sErr;
        markSession(type);
      }
      retryRef.current = null;
      setLink({ accessToken, type });
    } catch (err) {
      if (isRetryable(err)) {
        retryRef.current = { flow: 'B', accessToken, refreshToken, type };
        setLink({ retry: true, reason: RETRY_REASON });
      } else {
        setLink({ invalid: true, reason: 'This link has expired or was already used.' });
      }
    }
  };

  const retryInstall = () => {
    const pending = retryRef.current;
    if (!pending) return;
    setLink(null); // back to the resolving state
    if (pending.flow === 'A') installFromTokenHash(pending);
    else installFromHashTokens(pending);
  };

  useEffect(() => {
    if (resolvedRef.current) return;
    resolvedRef.current = true;

    const { tokenHash, accessToken, refreshToken, type, errorDescription } = parseParams();
    const scrub = () => {
      // Drop the token from the address bar / history without a new entry.
      if (window.location.hash || window.location.search) {
        window.history.replaceState(null, '', window.location.pathname);
      }
    };

    (async () => {
      if (errorDescription) { setLink({ invalid: true, reason: errorDescription }); scrub(); return; }

      if (tokenHash) {
        await installFromTokenHash({ tokenHash, type });
        scrub();
        return;
      }

      if (accessToken) {
        // Scrub BEFORE creating the client so its detectSessionInUrl
        // can't race the explicit install (tokens stay in memory for
        // the retry path).
        scrub();
        await installFromHashTokens({ accessToken, refreshToken, type });
        return;
      }

      // No token in the URL. If THIS page previously installed a session
      // (marker set by the installers above) — e.g. the service worker
      // deploy-refreshed us after the one-time token was consumed and
      // the URL scrubbed, or the user hit F5 — recover the persisted
      // session instead of dead-ending a link that already verified.
      try {
        const marker = sessionStorage.getItem(RESET_MARKER_KEY);
        if (marker) {
          const { data } = await getSupabaseClient().auth.getSession();
          const at = data?.session?.access_token;
          if (at) { setLink({ accessToken: at, type: marker }); return; }
        }
      } catch { /* fall through to the invalid card */ }
      setLink({ invalid: true, reason: 'No reset token found in the link.' });
      scrub();
    })();
    // The installers are stable for the lifetime of this mount; the effect
    // is a one-shot resolver by design (resolvedRef).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isInvite = link?.type === 'invite';

  // Same show/hide eye the sign-in page uses (styles come from Home.css,
  // which this page already imports).
  const EyeIcon = ({ off }) => off ? (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
      <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
      <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </svg>
  ) : (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const supabase = getSupabaseClient();
      // 1) Set the password on the recovery/invite session DIRECTLY.
      //    updateUser works at aal1 — that's exactly what recovery/invite
      //    sessions are for. Doing it here (not via the MFA-enforced
      //    /api/auth/exchange) means a user flagged require-2FA can still
      //    set their password; the exchange would 403 them (MFA_REQUIRED).
      const { error: updErr } = await supabase.auth.updateUser({ password });
      if (updErr) throw updErr;
      // 2) Try to sign straight in.
      const { data: sess } = await supabase.auth.getSession();
      const accessToken = sess?.session?.access_token || link.accessToken;
      try {
        const data = await api.authExchange(accessToken);
        if (!data?.token) throw new Error('No token returned');
        clearMarker();
        api.setToken(data.token);
        await login(data.token);
        navigate(HOME_URL, { replace: true });
      } catch (exErr) {
        // Password IS saved now. If the account requires 2FA, the exchange
        // refuses this aal1 session — hand off to the sign-in page, where
        // the password they just set flows through the normal challenge/
        // enrolment. Any other error is surfaced.
        if (exErr?.response?.status === 403 && exErr?.response?.data?.code === 'MFA_REQUIRED') {
          clearMarker();
          setMfaHandoff(true);
          setSubmitting(false);
          return;
        }
        throw exErr;
      }
    } catch (err) {
      const raw = err?.response?.data?.error || err?.message || 'Something went wrong';
      // An expired/used link fails at verifyOtp / updateUser.
      setError(/invalid or expired|not found|expired/i.test(raw)
        ? 'This link has expired or was already used. Request a new one below.'
        : raw);
      setSubmitting(false);
    }
  };

  if (!link) return null;

  // Password saved, but the account requires 2FA — the exchange refused
  // this aal1 session. Send them to sign in, where the new password runs
  // through the normal challenge/enrolment.
  if (mfaHandoff) {
    return (
      <div className="landing">
        <div className="landing-card">
          <div className="landing-brand">
            <img src={strideLogo} alt="Zebra Stride" className="landing-brand-logo" />
          </div>
          <p className="landing-subtitle">Password saved</p>
          <div className="landing-login-form">
            <div className="landing-login-info">
              This account requires two-factor authentication. Sign in with your
              new password to finish setting it up.
            </div>
            <button type="button" className="landing-cta" onClick={() => { clearMarker(); navigate('/', { replace: true }); }}>
              Go to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="landing">
      <div className="landing-card">
        <div className="landing-brand">
          <img src={strideLogo} alt="Zebra Stride" className="landing-brand-logo" />
        </div>
        {isInvite && <p className="landing-welcome">Welcome</p>}
        <p className="landing-subtitle">
          {link.invalid
            ? 'Password link problem'
            : link.retry
              ? 'Connection problem'
              : isInvite ? 'Set a password to finish creating your account' : 'Choose a new password'}
        </p>

        {(link.invalid || link.retry) ? (
          <div className="landing-login-form">
            <div className="landing-login-error">
              {link.reason}
              {link.invalid && (
                <>
                  {' '}Invite and reset links only work once and expire after a short time —
                  ask your administrator for a new invite, or request a reset from the sign-in page.
                </>
              )}
            </div>
            {link.retry ? (
              <button type="button" className="landing-cta" onClick={retryInstall}>
                Try again
              </button>
            ) : (
              <button type="button" className="landing-cta" onClick={() => { clearMarker(); navigate('/', { replace: true }); }}>
                Back to sign in
              </button>
            )}
          </div>
        ) : (
          <form className="landing-login-form" onSubmit={handleSubmit}>
            <div className="landing-password-wrap">
              <input
                type={showPassword ? 'text' : 'password'}
                className="landing-login-input landing-password-input"
                placeholder="New password (min 8 characters)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                required
                disabled={submitting}
                autoFocus
              />
              <button
                type="button"
                className="landing-password-toggle"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                aria-pressed={showPassword}
                title={showPassword ? 'Hide password' : 'Show password'}
                disabled={submitting}
              >
                <EyeIcon off={showPassword} />
              </button>
            </div>
            <div className="landing-password-wrap">
              <input
                type={showConfirm ? 'text' : 'password'}
                className="landing-login-input landing-password-input"
                placeholder="Confirm new password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                required
                disabled={submitting}
              />
              <button
                type="button"
                className="landing-password-toggle"
                onClick={() => setShowConfirm((v) => !v)}
                aria-label={showConfirm ? 'Hide password' : 'Show password'}
                aria-pressed={showConfirm}
                title={showConfirm ? 'Hide password' : 'Show password'}
                disabled={submitting}
              >
                <EyeIcon off={showConfirm} />
              </button>
            </div>
            {error && <div className="landing-login-error">{error}</div>}
            <button type="submit" className="landing-cta" disabled={submitting}>
              {submitting ? 'Saving…' : (isInvite ? 'Set password & sign in' : 'Save new password')}
            </button>
            <button
              type="button"
              className="landing-login-link"
              onClick={() => {
                // Abandoning the flow: clear the recovery marker AND the
                // installed session so the next person on this tab can't
                // reopen the form and set THIS user's password.
                clearMarker();
                try { getSupabaseClient().auth.signOut({ scope: 'local' }).catch(() => {}); } catch { /* unconfigured */ }
                navigate('/', { replace: true });
              }}
              disabled={submitting}
            >
              Back to sign in
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
